<script setup lang="ts">
import { computed } from 'vue';
import { useCornerDock } from '@/composables/useCornerDock';

// The single docking point for the app's floating banners. Each banner used to
// pin itself to a corner, which meant they landed on top of one another and on
// the AI chat launcher — every corner of this app is already claimed by
// something (toasts top-right, the sidebar bottom-left, the chat widget
// bottom-right). Banners now render as plain cards into this stack, and only
// this component decides where the corner is.
//
// How far up to sit comes from useCornerDock rather than from any assumption
// about who else is on screen, so the stack is correct whether the chat widget
// is absent, collapsed to its launcher, or expanded.
const { clearance, coversViewport } = useCornerDock();

// Gutter between the stack and whatever holds the corner below it.
const GUTTER_PX = 16;

const bottomOffset = computed(() => `${clearance.value + GUTTER_PX}px`);
</script>

<template>
  <!-- pointer-events-none so an empty (or gap) area of the stack never swallows
       clicks meant for the page; each banner re-enables them for its own card.
       The max-width clamp keeps a max-w-sm card from overflowing a narrow phone. -->
  <div
    v-if="!coversViewport"
    class="fixed right-4 z-[1000] flex max-w-[calc(100vw-2rem)] flex-col items-end gap-3 pointer-events-none"
    :style="{ bottom: bottomOffset }"
    data-test="floating-banner-stack"
  >
    <slot />
  </div>
</template>
